import type { HelpArticle, Ticket, TicketCategory } from '../types/tickets'

export const ticketCategories: TicketCategory[] = [
  'Account and sign-in',
  'Housing',
  'Marketplace order',
  'Money and CampaGift',
  'Academic services',
  'Report a person',
  'Report a listing',
  'Report a transaction',
  'Suspicious activity',
  'Something else',
]

/** Categories that are a report about someone or something, not a question. */
export const reportCategories: TicketCategory[] = [
  'Report a person',
  'Report a listing',
  'Report a transaction',
  'Suspicious activity',
]

export const helpArticles: HelpArticle[] = [
  {
    id: 'help-1',
    title: 'I cannot sign in',
    summary:
      'Try recovery from the sign-in page first. A wrong password and an unknown account give the same message on purpose, so recovery is the only way to tell them apart.',
    category: 'Account and sign-in',
  },
  {
    id: 'help-2',
    title: 'What does “not inspected by Campuzon” mean on a listing?',
    summary:
      'It means no representative has visited the property. The landlord entered the information, and nobody has confirmed it. Treat rent, photographs and availability as unverified.',
    category: 'Housing',
  },
  {
    id: 'help-3',
    title: 'A landlord asked me for an inspection fee',
    summary:
      'Do not pay it. No legitimate agent charges to show a room, and Campuzon never collects one. Report the listing and we will review it.',
    category: 'Report a listing',
  },
  {
    id: 'help-4',
    title: 'My order is stuck in escrow',
    summary:
      'Escrow releases on your approval, on automatic completion after the review window, or on a resolved dispute. Open the order to see which state it is in and what happens next.',
    category: 'Marketplace order',
    featureKey: 'escrow',
  },
  {
    id: 'help-5',
    title: 'When can I withdraw my balance?',
    summary:
      'Your ledger records what you are entitled to. Withdrawal is performed by a licensed provider and is not enabled yet, so no balance can currently be paid out.',
    category: 'Money and CampaGift',
    featureKey: 'payouts',
  },
  {
    id: 'help-6',
    title: 'My CampaGift will not redeem at a merchant',
    summary:
      'The redemption desk shows every rule it checked. Most refusals are merchant eligibility, category scope, expiry, or a redemption cap — each is named on screen.',
    category: 'Money and CampaGift',
  },
  {
    id: 'help-7',
    title: 'Someone asked for my university password',
    summary:
      'Never give it to anyone. Campuzon never asks for it, and no provider is permitted to request it. Report the person immediately — we treat this as an integrity breach.',
    category: 'Suspicious activity',
  },
  {
    id: 'help-8',
    title: 'How long does a transcript request take?',
    summary:
      'The processing estimate comes from the institution, not from us. Where a university has not published one, the service says so rather than guessing.',
    category: 'Academic services',
  },
]

export const seedTickets: Ticket[] = [
  {
    id: 'tkt-1',
    reference: 'SUP-104871',
    subject: 'Room does not match the photographs',
    category: 'Report a listing',
    status: 'With support',
    priority: 'High',
    openedAt: '2026-08-11T09:24:00Z',
    updatedAt: '2026-08-13T15:02:00Z',
    raisedBy: 'saviour.a',
    assignedTo: 'Trust & safety — Adaeze',
    context: {
      kind: 'listing',
      reference: 'LST-2261',
      label: 'Ceeplus Court, Iwaya — 4-bed shared',
      surface: '/housing/lst-2261',
    },
    messages: [
      {
        id: 'msg-1',
        author: 'you',
        authorName: 'You',
        body: 'I inspected the room today. It has three beds, not four as listed, and there is no running water in the shared bathroom.',
        at: '2026-08-11T09:24:00Z',
      },
      {
        id: 'msg-2',
        author: 'support',
        authorName: 'Adaeze — Trust & safety',
        body: 'Thank you. The listing is now flagged and hidden from search while a representative re-verifies it. You will not be charged for the application, and it has been withdrawn.',
        at: '2026-08-11T14:10:00Z',
      },
      {
        id: 'msg-3',
        author: 'system',
        authorName: 'Campuzon',
        body: 'Verification status changed: Campuzon Verified → Under review.',
        at: '2026-08-11T14:11:00Z',
      },
      {
        id: 'msg-4',
        author: 'support',
        authorName: 'Adaeze — Trust & safety',
        body: 'A representative visits on Friday. I will come back to you with the outcome.',
        at: '2026-08-13T15:02:00Z',
      },
    ],
    resolution: null,
  },
  {
    id: 'tkt-2',
    reference: 'SUP-104655',
    subject: 'Charged twice for the same knowledge licence',
    category: 'Report a transaction',
    status: 'Resolved',
    priority: 'Normal',
    openedAt: '2026-07-28T11:02:00Z',
    updatedAt: '2026-08-02T10:20:00Z',
    raisedBy: 'saviour.a',
    assignedTo: 'Finance operations — Tunde',
    context: {
      kind: 'transaction',
      reference: 'TXN-0•••••41',
      label: 'Knowledge licence — Engineering Mathematics',
      surface: '/money',
      masked: true,
    },
    messages: [
      {
        id: 'msg-5',
        author: 'you',
        authorName: 'You',
        body: 'My ledger shows two identical entries for the same licence a few seconds apart.',
        at: '2026-07-28T11:02:00Z',
      },
      {
        id: 'msg-6',
        author: 'support',
        authorName: 'Tunde — Finance operations',
        body: 'Confirmed: a duplicate request was accepted before idempotency was applied to that route. A reversal has been recorded beside the original entry — the original is preserved, as ledger history is never edited.',
        at: '2026-08-01T09:40:00Z',
      },
      {
        id: 'msg-7',
        author: 'system',
        authorName: 'Campuzon',
        body: 'Reversal recorded. Your entitlement is unaffected.',
        at: '2026-08-01T09:41:00Z',
      },
    ],
    resolution:
      'Duplicate charge reversed with a compensating ledger entry. The original transaction remains in your history, as it must.',
  },
  {
    id: 'tkt-3',
    reference: 'SUP-104902',
    subject: 'Cannot verify my email address',
    category: 'Account and sign-in',
    status: 'Awaiting your reply',
    priority: 'Normal',
    openedAt: '2026-08-13T08:15:00Z',
    updatedAt: '2026-08-13T08:52:00Z',
    raisedBy: 'saviour.a',
    assignedTo: 'Support — Ngozi',
    context: { kind: 'none', reference: '—', label: 'No linked record', surface: '' },
    messages: [
      {
        id: 'msg-8',
        author: 'you',
        authorName: 'You',
        body: 'The six-digit code says it has expired every time, even immediately after I request it.',
        at: '2026-08-13T08:15:00Z',
      },
      {
        id: 'msg-9',
        author: 'support',
        authorName: 'Ngozi — Support',
        body: 'Codes last ten minutes and are single-use, so an immediate expiry usually means an older code is being entered. Could you confirm the time on the message you are reading from?',
        at: '2026-08-13T08:52:00Z',
      },
    ],
    resolution: null,
  },
]
